import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  JobKitSummary,
  JobKitDetail,
  JobKitCreateDto,
  JobKitItem,
  JobKitItemCreateDto,
  JobKitSearchDto,
  JobKitItemType,
} from '../../models/job-kit.interface';
import { ApiResponse } from '@app/core/adaptors/dto';
import {
  parametersFromPath,
  parametersFromRequest,
} from '@app/core/interceptors/mock-api.interceptor';

type StoredJobKit = JobKitDetail;

@Injectable({ providedIn: 'root' })
export class JobKitMocksService {
  private readonly STORAGE_KEY = 'mock_job_kits';
  private readonly ITEMS_STORAGE_KEY = 'mock_job_kit_items';
  private readonly SCHEMA_VERSION_KEY = 'mock_job_kits_schema_v';
  private readonly CURRENT_SCHEMA_VERSION = '2';
  private jobKits: StoredJobKit[] = [];
  private jobKitItems: JobKitItem[] = [];

  constructor() {
    this.checkSchemaVersion();
    this.loadJobKitsFromStorage();
    this.loadItemsFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const { tenantId, resourceId } = parametersFromPath(req, 'job-kits');

    // Handle items endpoints
    if (req.url.includes('/items')) {
      return this.handleItemsRequest(req, tenantId, resourceId);
    }

    // Handle job kits endpoints
    if (req.method === 'GET') {
      if (resourceId) {
        return this.getJobKitById(resourceId);
      } else {
        const queryParams = parametersFromRequest(req);
        const searchDto = { ...queryParams };
        return this.getJobKits(tenantId, searchDto);
      }
    } else if (req.method === 'POST') {
      const saveDto = req.body as JobKitCreateDto;
      return this.saveJobKit(tenantId, saveDto);
    } else if (req.method === 'PUT' && resourceId) {
      const updatedJobKit = req.body as Partial<JobKitCreateDto>;
      return this.updateJobKit(resourceId, updatedJobKit);
    } else if (req.method === 'DELETE' && resourceId) {
      return this.deleteJobKit(resourceId);
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  private handleItemsRequest(
    req: HttpRequest<any>,
    tenantId: number,
    jobKitId?: number
  ): Observable<HttpEvent<any>> {
    const matches = req.url.match(/\/job-kits\/(\d+)\/items(?:\/(\d+))?/);
    const kitId = matches ? parseInt(matches[1], 10) : jobKitId;
    const itemId = matches ? (matches[2] ? parseInt(matches[2], 10) : undefined) : undefined;

    if (req.method === 'GET') {
      if (itemId) {
        return this.getJobKitItemById(itemId);
      } else {
        return this.getJobKitItems(kitId!);
      }
    } else if (req.method === 'POST') {
      const itemDto = req.body as JobKitItemCreateDto;
      return this.saveJobKitItem(tenantId, kitId!, itemDto);
    } else if (req.method === 'PUT' && itemId) {
      const updatedItem = req.body as Partial<JobKitItemCreateDto>;
      return this.updateJobKitItem(itemId, updatedItem);
    } else if (req.method === 'DELETE' && itemId) {
      return this.deleteJobKitItem(itemId);
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  saveJobKit(tenantId: number, jobKit: JobKitCreateDto): Observable<HttpEvent<any>> {
    const newJobKit: StoredJobKit = {
      id: this.getNextJobKitId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tenantId,
      ...jobKit,
    };
    this.jobKits.push(newJobKit);
    this.saveJobKitsToStorage();
    return this.mapToResponse(newJobKit, 201);
  }

  updateJobKit(id: number, updatedFields: Partial<JobKitCreateDto>): Observable<HttpEvent<any>> {
    const existingIndex = this.jobKits.findIndex((jk) => jk.id === id);
    if (existingIndex !== -1) {
      this.jobKits[existingIndex] = {
        ...this.jobKits[existingIndex],
        ...updatedFields,
        updatedAt: new Date().toISOString(),
      };
    }
    this.saveJobKitsToStorage();
    return this.mapToResponse(this.jobKits[existingIndex], 200);
  }

  deleteJobKit(jobKitId: number): Observable<HttpEvent<any>> {
    this.jobKits = this.jobKits.filter((jk) => jk.id !== jobKitId);
    this.jobKitItems = this.jobKitItems.filter((item) => item.jobKitId !== jobKitId);
    this.saveJobKitsToStorage();
    this.saveItemsToStorage();
    return this.mapToResponse({}, 200);
  }

  getJobKits(tenantId: number, searchCriteria: JobKitSearchDto): Observable<HttpEvent<any>> {
    const { limit, page, query, sortField, sortOrder } = searchCriteria;

    if (this.jobKits.length === 0) {
      this.generateMockJobKits();
      this.saveJobKitsToStorage();
    }

    const filteredData = this.jobKits
      .filter((jk) => jk.tenantId === tenantId && anyQueryMatch(jk, query))
      .sort((a, b) => {
        const aValue = a[sortField as keyof StoredJobKit];
        const bValue = b[sortField as keyof StoredJobKit];
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortOrder === 'asc'
          ? (aValue as any) - (bValue as any)
          : (bValue as any) - (aValue as any);
      });

    const data = filteredData.slice((page - 1) * limit, page * limit);

    // Convert to summary for list view
    const summaries: JobKitSummary[] = data.map((jk) => ({
      id: jk.id,
      name: jk.name,
      keycode: jk.keycode,
      description: jk.description || '',
      totalCost: jk.totalCost,
      discount: jk.discount,
      summarize: jk.summarize,
      createdAt: jk.createdAt,
    }));

    const res: ApiResponse<JobKitSummary> = {
      data: summaries,
      total: filteredData.length,
      limit,
      page,
      totalPages: Math.ceil(filteredData.length / limit),
    };

    return this.mapToResponse(res, 200);
  }

  getJobKitById(id: number): Observable<HttpEvent<any>> {
    const jobKit = this.jobKits.find((jk) => jk.id === id);
    if (jobKit) {
      return this.mapToResponse(jobKit, 200);
    } else {
      return this.mapToResponse({ message: 'Job Kit not found' }, 404);
    }
  }

  saveJobKitItem(
    tenantId: number,
    jobKitId: number,
    item: JobKitItemCreateDto
  ): Observable<HttpEvent<any>> {
    const newItem: JobKitItem = {
      id: this.getNextItemId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tenantId,
      jobKitId,
      labourCategoryName: null,
      ...item,
    };
    this.jobKitItems.push(newItem);
    this.saveItemsToStorage();
    return this.mapToResponse(newItem, 201);
  }

  updateJobKitItem(
    itemId: number,
    updatedFields: Partial<JobKitItemCreateDto>
  ): Observable<HttpEvent<any>> {
    const existingIndex = this.jobKitItems.findIndex((item) => item.id === itemId);
    if (existingIndex !== -1) {
      this.jobKitItems[existingIndex] = {
        ...this.jobKitItems[existingIndex],
        ...updatedFields,
        updatedAt: new Date().toISOString(),
      };
    }
    this.saveItemsToStorage();
    return this.mapToResponse(this.jobKitItems[existingIndex], 200);
  }

  deleteJobKitItem(itemId: number): Observable<HttpEvent<any>> {
    this.jobKitItems = this.jobKitItems.filter((item) => item.id !== itemId);
    this.saveItemsToStorage();
    return this.mapToResponse({}, 200);
  }

  getJobKitItems(jobKitId: number): Observable<HttpEvent<any>> {
    const items = this.jobKitItems.filter((item) => item.jobKitId === jobKitId);
    const res: ApiResponse<JobKitItem> = {
      data: items,
      total: items.length,
      page: 1,
      limit: items.length || 10,
      totalPages: 1,
    };
    return this.mapToResponse(res, 200);
  }

  getJobKitItemById(id: number): Observable<HttpEvent<any>> {
    const item = this.jobKitItems.find((item) => item.id === id);
    if (item) {
      return this.mapToResponse(item, 200);
    } else {
      return this.mapToResponse({ message: 'Job Kit Item not found' }, 404);
    }
  }

  getMockJobKits(): StoredJobKit[] {
    if (this.jobKits.length === 0) {
      this.generateMockJobKits();
      this.saveJobKitsToStorage();
    }
    return [...this.jobKits];
  }

  private checkSchemaVersion(): void {
    const storedVersion = localStorage.getItem(this.SCHEMA_VERSION_KEY);
    if (storedVersion !== this.CURRENT_SCHEMA_VERSION) {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.ITEMS_STORAGE_KEY);
      localStorage.setItem(this.SCHEMA_VERSION_KEY, this.CURRENT_SCHEMA_VERSION);
      console.log('Job kits schema version changed, cleared localStorage');
    }
  }

  private loadJobKitsFromStorage(): void {
    try {
      const storedJobKits = localStorage.getItem(this.STORAGE_KEY);
      if (storedJobKits) {
        this.jobKits = JSON.parse(storedJobKits);
        console.log(`Loaded ${this.jobKits.length} job kits from localStorage`);
      }
    } catch (error) {
      console.error('Error loading job kits from localStorage:', error);
      this.jobKits = [];
    }
  }

  private saveJobKitsToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.jobKits));
      console.log(`Saved ${this.jobKits.length} job kits to localStorage`);
    } catch (error) {
      console.error('Error saving job kits to localStorage:', error);
    }
  }

  private loadItemsFromStorage(): void {
    try {
      const storedItems = localStorage.getItem(this.ITEMS_STORAGE_KEY);
      if (storedItems) {
        this.jobKitItems = JSON.parse(storedItems);
        console.log(`Loaded ${this.jobKitItems.length} job kit items from localStorage`);
      }
    } catch (error) {
      console.error('Error loading job kit items from localStorage:', error);
      this.jobKitItems = [];
    }
  }

  private saveItemsToStorage(): void {
    try {
      localStorage.setItem(this.ITEMS_STORAGE_KEY, JSON.stringify(this.jobKitItems));
      console.log(`Saved ${this.jobKitItems.length} job kit items to localStorage`);
    } catch (error) {
      console.error('Error saving job kit items to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(delay(500)); // Simulate network delay
  }

  private generateMockJobKits(): void {
    for (let i = 1; i <= 15; i++) {
      this.jobKits.push(this.newJobKit(i));
      // Generate items for each job kit
      const itemCount = faker.number.int({ min: 2, max: 6 });
      for (let j = 1; j <= itemCount; j++) {
        this.jobKitItems.push(this.newJobKitItem(i, j));
      }
    }
  }

  private newJobKit(jobKitId: number): StoredJobKit {
    return {
      id: jobKitId,
      tenantId: 1,
      keycode: `JK${String(jobKitId).padStart(4, '0')}`,
      name: faker.commerce.productName(),
      description: faker.lorem.sentences(2),
      totalCost: parseFloat(faker.commerce.price({ min: 50, max: 2000 })),
      discount: faker.number.float({ min: 0, max: 20, fractionDigits: 2 }),
      summarize: faker.datatype.boolean(),
      invoiceText: faker.lorem.sentence(),
      createdAt: faker.date.past().toISOString(),
      updatedAt: faker.date.past().toISOString(),
    };
  }

  private newJobKitItem(jobKitId: number, itemIndex: number): JobKitItem {
    const typeOptions: JobKitItemType[] = [
      JobKitItemType.PART,
      JobKitItemType.LABOUR,
      JobKitItemType.TEXT,
      JobKitItemType.MISCELLANEOUS,
    ];
    const type = faker.helpers.arrayElement(typeOptions);
    const labourCategoryNames = ['Mechanical', 'Electrical', 'Diagnostics', 'Body Work', 'General'];
    return {
      id: jobKitId * 100 + itemIndex,
      tenantId: 1,
      jobKitId,
      type,
      code: `SKU${String(jobKitId * 100 + itemIndex).padStart(6, '0')}`,
      description: faker.commerce.productName(),
      quantity: faker.number.float({ min: 1, max: 50, fractionDigits: 2 }),
      unitCost: parseFloat(faker.commerce.price({ min: 5, max: 100 })),
      totalCost: parseFloat(faker.commerce.price({ min: 10, max: 500 })),
      discount: faker.number.float({ min: 0, max: 15, fractionDigits: 2 }),
      labourCategoryId:
        type === JobKitItemType.LABOUR ? faker.number.int({ min: 1, max: 8 }) : null,
      labourCategoryName:
        type === JobKitItemType.LABOUR ? faker.helpers.arrayElement(labourCategoryNames) : null,
      createdAt: faker.date.past().toISOString(),
      updatedAt: faker.date.past().toISOString(),
    };
  }

  private getNextJobKitId(): number {
    return this.jobKits.length > 0 ? Math.max(...this.jobKits.map((jk) => jk.id)) + 1 : 1;
  }

  private getNextItemId(): number {
    return this.jobKitItems.length > 0
      ? Math.max(...this.jobKitItems.map((item) => item.id)) + 1
      : 1;
  }
}

const anyQueryMatch = (jobKit: StoredJobKit, query: string): boolean => {
  if (!query?.trim()) {
    return true;
  }

  const search = query.toLowerCase().trim();
  const isNameMatch = jobKit.name?.toLowerCase().includes(search) ?? false;
  const isKeycodeMatch = jobKit.keycode?.toLowerCase().includes(search) ?? false;
  const isDescriptionMatch = jobKit.description?.toLowerCase().includes(search) ?? false;

  return isNameMatch || isKeycodeMatch || isDescriptionMatch;
};
